import { useEffect, useState } from "react";
import { Clock, Building2, Loader2, Save, Plus, Trash2, Lock, MapPin, Layers } from "lucide-react";
import { toast } from "sonner";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { api } from "@/lib/api";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", LINE = "#E7E1D6";
const labelCls = "block text-xs font-semibold uppercase tracking-wide text-fundio-slate";

export default function Oeffnungszeiten() {
  const [c, setC] = useState(null);
  const [role, setRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [denied, setDenied] = useState(false);
  const [mode, setMode] = useState("loading");      // loading | single | location | overview
  const [activeLoc, setActiveLoc] = useState(null);  // {id, name, city} in location mode
  const [overview, setOverview] = useState(null);    // [locations] in overview mode

  const canEdit = role === "owner" || role === "admin";

  const loadSingle = () => api.get("/auth/contact").then(({ data }) => setC(data)).catch((e) => { if (e.response?.status === 403) setDenied(true); });
  const loadLocation = (lid) => api.get(`/locations/${lid}/contact`).then(({ data }) => setC(data)).catch((e) => { if (e.response?.status === 403) setDenied(true); });
  const loadOverview = () => api.get("/locations/contacts").then(({ data }) => setOverview(data.locations || [])).catch(() => setOverview([]));

  useEffect(() => {
    api.get("/org/current").then(({ data }) => { if (data.status === "ok") setRole(data.tenant.role); }).catch(() => {});
    api.get("/me/locations").then(({ data }) => {
      if (!data.multi) { setMode("single"); loadSingle(); return; }
      if (data.active_location_id) {
        setActiveLoc((data.locations || []).find((l) => l.id === data.active_location_id) || { id: data.active_location_id, name: "Standort" });
        setMode("location");
        loadLocation(data.active_location_id);
      } else {
        setMode("overview");
        loadOverview();
      }
    }).catch(() => { setMode("single"); loadSingle(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const setHour = (i, j, v) => setC((p) => { const h = p.hours.map((r) => [...r]); h[i][j] = v; return { ...p, hours: h }; });
  const addHour = () => setC((p) => ({ ...p, hours: [...(p.hours || []), ["", ""]] }));
  const removeHour = (i) => setC((p) => ({ ...p, hours: p.hours.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: c.name, tagline: c.tagline || "", phoneDisplay: c.phoneDisplay, mobileDisplay: c.mobileDisplay || "",
        email: c.email, city: c.city, address: c.address, region: c.region || "Schweiz",
        hours: (c.hours || []).filter((r) => r[0]),
      };
      const url = mode === "location" ? `/locations/${activeLoc.id}/contact` : "/auth/contact";
      const { data } = await api.put(url, body);
      setC(data);
      toast.success("Öffnungszeiten & Kontaktdaten gespeichert.");
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  const field = (label, key, placeholder) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input data-testid={`contact-${key}`} value={c[key] || ""} onChange={(e) => set(key, e.target.value)} placeholder={placeholder} className="fundio-input mt-1.5" disabled={!canEdit} />
    </div>
  );

  const editor = (
    c ? (
      <div className="grid gap-6 lg:grid-cols-2" data-testid="oeffnungszeiten-editor">
        <div className="fundio-panel">
          <h2 className="mb-4 flex items-center gap-2 text-[17px] font-bold" style={{ color: INK }}><Building2 size={17} style={{ color: GOLD }} /> Kontaktdaten</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {field("Firmenname", "name", "Fundio")}
            {field("Slogan", "tagline", "Entdecken. Stöbern.")}
            {field("Telefon", "phoneDisplay", "+41 31 …")}
            {field("Mobil / WhatsApp", "mobileDisplay", "+41 79 …")}
            {field("E-Mail", "email", "info@…")}
            {field("Ort", "city", "Ueberstorf")}
            <div className="sm:col-span-2">{field("Adresse", "address", "Strasse, PLZ Ort")}</div>
            {field("Region", "region", "Schweiz")}
          </div>
        </div>

        <div className="fundio-panel">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-[17px] font-bold" style={{ color: INK }}><Clock size={17} style={{ color: GOLD }} /> Öffnungszeiten</h2>
            {canEdit && <button data-testid="contact-add-hour" onClick={addHour} className="fundio-action" style={{ minHeight: 36, padding: "6px 12px", fontSize: 13 }}><Plus size={13} /> Zeile</button>}
          </div>
          <div className="mt-4 space-y-2">
            {(c.hours || []).map((row, i) => (
              <div key={i} className="flex gap-2" data-testid={`contact-hour-row-${i}`}>
                <input data-testid={`contact-hour-day-${i}`} value={row[0]} onChange={(e) => setHour(i, 0, e.target.value)} placeholder="Tag(e)" className="fundio-input" disabled={!canEdit} />
                <input data-testid={`contact-hour-time-${i}`} value={row[1]} onChange={(e) => setHour(i, 1, e.target.value)} placeholder="12:00 – 17:00" className="fundio-input" disabled={!canEdit} />
                {canEdit && <button data-testid={`contact-hour-remove-${i}`} onClick={() => removeHour(i)} className="fundio-action shrink-0" style={{ minWidth: 48, color: "#903824", borderColor: "#E7B9AF" }}><Trash2 size={14} /></button>}
              </div>
            ))}
            {(!c.hours || c.hours.length === 0) && <p className="text-sm" style={{ color: SLATE }}>Noch keine Öffnungszeiten hinterlegt.</p>}
          </div>
          {canEdit && (
            <button data-testid="contact-save-btn" onClick={save} disabled={saving} className="fundio-action fundio-action--gold mt-6">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Speichern
            </button>
          )}
        </div>
      </div>
    ) : (
      <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
    )
  );

  return (
    <CockpitLayout active="oeffnungszeiten" title="Öffnungszeiten">
      <div className="fundio-page-stack" data-testid="gold-oeffnungszeiten">
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title flex items-center gap-2.5" data-testid="oeffnungszeiten-title">
              <Clock size={28} style={{ color: GOLD }} /> Öffnungszeiten & Kontakt
            </h1>
            <p className="mt-1 fundio-muted text-[14px]">Diese Angaben erscheinen auf deiner öffentlichen Brocki-Seite.</p>
          </div>
        </div>

        {mode === "loading" ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
        ) : denied ? (
          <div className="fundio-panel flex items-center gap-2.5" style={{ color: MUTED }} data-testid="oeffnungszeiten-denied">
            <Lock size={16} style={{ color: SLATE }} /> Nur Inhaber und Admins können Öffnungszeiten & Kontaktdaten bearbeiten.
          </div>
        ) : mode === "overview" ? (
          <div data-testid="oeffnungszeiten-overview" className="flex flex-col gap-5">
            <div className="flex items-start gap-2.5 rounded-xl border p-3.5" style={{ borderColor: "rgba(216,154,0,.4)", background: "rgba(216,154,0,.06)", color: MUTED }} data-testid="overview-hint">
              <Layers size={16} className="mt-0.5 shrink-0" style={{ color: GOLD }} />
              <span className="text-[13px]">Übersicht aller Standorte (schreibgeschützt). Wähle oben rechts einen <b>Standort</b>, um dessen Öffnungszeiten & Kontakt zu bearbeiten.</span>
            </div>
            {overview === null ? (
              <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                {overview.map((l) => (
                  <div key={l.id} className="fundio-panel" data-testid={`overview-loc-${l.id}`}>
                    <div className="flex items-center gap-2">
                      <MapPin size={16} style={{ color: GOLD }} />
                      <h3 className="text-[15px] font-bold" style={{ color: INK }}>{l.name}</h3>
                      {l.is_primary && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={{ background: "rgba(216,154,0,.12)", color: GOLD }}>Primär</span>}
                    </div>
                    <div className="mt-3 space-y-1 text-[13px]" style={{ color: MUTED }}>
                      {l.address && <p>{l.address}</p>}
                      {l.phoneDisplay && <p>Tel: {l.phoneDisplay}</p>}
                      {l.email && <p>{l.email}</p>}
                    </div>
                    <div className="mt-3 border-t pt-3" style={{ borderColor: LINE }}>
                      {(l.hours || []).length ? (l.hours || []).map((r, i) => (
                        <p key={i} className="flex justify-between text-[13px]" style={{ color: INK }}><span>{r[0]}</span><span style={{ color: SLATE }}>{r[1]}</span></p>
                      )) : <p className="text-[13px]" style={{ color: SLATE }}>Keine Öffnungszeiten hinterlegt.</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {mode === "location" && activeLoc && (
              <div className="inline-flex w-fit items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: "rgba(216,154,0,.4)", background: "rgba(216,154,0,.06)" }} data-testid="location-context-badge">
                <MapPin size={15} style={{ color: GOLD }} />
                <span className="text-[13px] font-semibold" style={{ color: INK }}>Standort: {activeLoc.name}{activeLoc.city ? ` · ${activeLoc.city}` : ""}</span>
              </div>
            )}
            {editor}
          </>
        )}
      </div>
    </CockpitLayout>
  );
}
